import React, { useState, useRef, useEffect } from 'react';
import { Inbox, Upload, Loader, CheckCircle2, X, Sparkles, FileText } from 'lucide-react';
import { piecesJointesApi } from '../../utils/api';
import { takePendingFile } from './depotShared';

const CATEGORIES = [
  { key: 'sante', label: 'Santé' },
  { key: 'contrat', label: 'Contrat' },
  { key: 'devis', label: 'Devis' },
  { key: 'administratif', label: 'Administratif' },
  { key: 'protocole', label: 'Protocole' },
  { key: 'autre', label: 'Autre' },
];

export default function DepotPage() {
  const [analyse, setAnalyse] = useState(null); // { proposition, enfants, protocoles, structures }
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [form, setForm] = useState(null); // { categorie, cible_type, enfant_id, protocole_id, structure_id }
  const fileRef = useRef(null);

  const onFile = async (file) => {
    if (!file) return;
    setBusy(true); setMsg(null); setAnalyse(null);
    try {
      const fd = new FormData();
      fd.append('fichier', file);
      const { data } = await piecesJointesApi.analyser(fd);
      setAnalyse(data);
      const p = data.proposition;
      setForm({ categorie: p.categorie || 'autre', cible_type: p.cible_type || 'autre', enfant_id: p.enfant_id || '', protocole_id: p.protocole_id || '', structure_id: p.structure_id || '' });
    } catch (e) { setMsg({ type: 'error', text: e.response?.data?.error || 'Analyse impossible' }); }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  // Fichier transmis depuis l'action rapide du tableau de bord
  useEffect(() => { const f = takePendingFile(); if (f) onFile(f); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const entiteFromForm = () => {
    if (form.cible_type === 'enfant' && form.enfant_id) return { entite_type: 'enfant', entite_id: Number(form.enfant_id) };
    if (form.cible_type === 'protocole' && form.protocole_id) return { entite_type: 'protocole', entite_id: Number(form.protocole_id) };
    if (form.cible_type === 'structure' && form.structure_id) return { entite_type: 'structure', entite_id: Number(form.structure_id) };
    return { entite_type: 'divers', entite_id: 0 };
  };

  const classer = async () => {
    const ent = entiteFromForm();
    if ((form.cible_type === 'enfant' && !form.enfant_id) || (form.cible_type === 'protocole' && !form.protocole_id) || (form.cible_type === 'structure' && !form.structure_id)) {
      setMsg({ type: 'error', text: 'Choisissez la cible (enfant, protocole ou structure).' }); return;
    }
    setBusy(true); setMsg(null);
    try {
      await piecesJointesApi.classer({ temp: analyse.proposition.temp, nom_original: analyse.proposition.nom_original, categorie: form.categorie, ...ent });
      setMsg({ type: 'ok', text: 'Document classé avec succès.' });
      setAnalyse(null); setForm(null);
    } catch (e) { setMsg({ type: 'error', text: e.response?.data?.error || 'Classement impossible' }); }
    setBusy(false);
  };

  const jeter = async () => {
    try { await piecesJointesApi.jeter(analyse.proposition.temp); } catch { /* noop */ }
    setAnalyse(null); setForm(null); setMsg(null);
  };

  const p = analyse?.proposition;
  const cibleLabel = (t) => ({ enfant: 'un enfant', protocole: 'un protocole', structure: 'une structure', autre: 'le classeur général' }[t] || t);

  return (
    <div className="max-w-3xl">
      <div className="mb-4">
        <h1 className="font-serif text-[28px] leading-tight text-navy-900 flex items-center gap-2"><Inbox size={24} className="text-gold-600" /> Déposer un document</h1>
        <div className="w-12 h-0.5 bg-gold-400 rounded-full mt-2 mb-2.5" />
        <p className="text-navy-400 text-[13.5px]">Téléversez n'importe quel document : Hello Georges le lit et propose automatiquement où le classer (dossier d'un enfant, protocole, structure…).</p>
      </div>

      {!analyse && (
        <div className="card p-8 text-center border-2 border-dashed border-cream-300"
          onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}>
          <Inbox size={36} className="text-navy-200 mx-auto mb-3" />
          <p className="text-navy-500 text-sm mb-4">Glissez un document ici, ou</p>
          <button onClick={() => fileRef.current?.click()} disabled={busy} className="btn-primary inline-flex">
            {busy ? <Loader size={16} className="animate-spin" /> : <Upload size={16} />} Choisir un fichier
          </button>
          <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.odt,.rtf,.txt,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.webp,.heic" className="hidden" onChange={e => onFile(e.target.files?.[0])} />
          <p className="text-[11.5px] text-navy-300 mt-3">PDF, Word, Excel, image… (25 Mo max)</p>
        </div>
      )}

      {busy && analyse === null && <p className="text-center text-navy-400 text-sm mt-3">Lecture et analyse du document…</p>}

      {p && form && (
        <div className="card p-5">
          <div className="flex items-start gap-2 mb-3">
            <Sparkles size={18} className="text-gold-600 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-navy-800 flex items-center gap-2"><FileText size={15} /> {p.nom_original}</div>
              {p.resume && <p className="text-[13px] text-navy-500 mt-0.5">{p.resume}</p>}
              <p className="text-[12.5px] text-navy-400 mt-1">Proposition : classer dans <strong>{cibleLabel(form.cible_type)}</strong>{p.cible_nom ? ` (« ${p.cible_nom} »)` : ''} — catégorie <strong>{CATEGORIES.find(c => c.key === form.categorie)?.label || form.categorie}</strong>{p.confiance ? ` · confiance ${Math.round(p.confiance * 100)}%` : ''}.</p>
            </div>
            <button onClick={jeter} className="text-navy-300 hover:text-red-500" title="Abandonner"><X size={18} /></button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-gray-50 rounded-xl p-3">
            <div>
              <label className="block text-[12px] text-navy-500 mb-1">Rattacher à</label>
              <select value={form.cible_type} onChange={e => setForm(f => ({ ...f, cible_type: e.target.value }))} className="form-select text-sm w-full">
                <option value="enfant">Un enfant</option>
                <option value="protocole">Un protocole</option>
                <option value="structure">Une structure</option>
                <option value="autre">Classeur général</option>
              </select>
            </div>
            <div>
              <label className="block text-[12px] text-navy-500 mb-1">Catégorie</label>
              <select value={form.categorie} onChange={e => setForm(f => ({ ...f, categorie: e.target.value }))} className="form-select text-sm w-full">
                {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            {form.cible_type === 'enfant' && (
              <div className="sm:col-span-2">
                <label className="block text-[12px] text-navy-500 mb-1">Enfant</label>
                <select value={form.enfant_id} onChange={e => setForm(f => ({ ...f, enfant_id: e.target.value }))} className="form-select text-sm w-full">
                  <option value="">— Choisir —</option>
                  {analyse.enfants.map(e => <option key={e.id} value={e.id}>{e.nom}</option>)}
                </select>
              </div>
            )}
            {form.cible_type === 'protocole' && (
              <div className="sm:col-span-2">
                <label className="block text-[12px] text-navy-500 mb-1">Protocole</label>
                <select value={form.protocole_id} onChange={e => setForm(f => ({ ...f, protocole_id: e.target.value }))} className="form-select text-sm w-full">
                  <option value="">— Choisir —</option>
                  {analyse.protocoles.map(p => <option key={p.id} value={p.id}>{p.nom}</option>)}
                </select>
              </div>
            )}
            {form.cible_type === 'structure' && (
              <div className="sm:col-span-2">
                <label className="block text-[12px] text-navy-500 mb-1">Structure</label>
                <select value={form.structure_id} onChange={e => setForm(f => ({ ...f, structure_id: e.target.value }))} className="form-select text-sm w-full">
                  <option value="">— Choisir —</option>
                  {analyse.structures.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
                </select>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 mt-4">
            <button onClick={classer} disabled={busy} className="btn-primary">{busy ? <Loader size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Classer le document</button>
            <button onClick={jeter} className="btn-secondary">Abandonner</button>
          </div>
        </div>
      )}

      {msg && <div className={`mt-3 text-[13px] rounded-lg px-3 py-2 ${msg.type === 'error' ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'}`}>{msg.text}</div>}
    </div>
  );
}
